import { useQuery } from '@tanstack/react-query'
import type {
  PassDecision,
  PassRule,
  PublicationKind,
  RectificationStatus,
  RankingDiff,
  RankingStatus,
  ResultSummary,
} from '@concours/shared'
import { useAuth } from '@concours/ui'

/** Calcul et classements (phase 15). */
export const RESULTS_KEY = ['admin', 'results'] as const

export interface ResultsStage {
  id: string
  label: string
  position: number
  status: string
  statusLabel: string
  competition: { id: string; reference: string; title: string }
  ranking: {
    version: number
    status: RankingStatus
    statusLabel: string
    summary: Partial<ResultSummary>
  } | null
}

export interface RankingVersion {
  id: string
  version: number
  status: RankingStatus
  statusLabel: string
  ruleVersion: number
  summary: Partial<ResultSummary>
  reason: string | null
  computedBy: string
  computedAt: string | null
}

export interface ResultsOverview {
  stage: {
    id: string
    label: string
    position: number
    status: string
    statusLabel: string
    graded: boolean
    anonymous: boolean
  }
  competition: { id: string; reference: string; title: string }
  population: number
  sheets: { total: number; validated: number }
  rules: { validated: number | null; draft: number | null }
  blockers: string[]
  versions: RankingVersion[]
  can: { calculate: boolean }
}

export interface EntryView {
  id: string
  stageCandidateId: string
  label: string
  name: string | null
  applicationNumber: string | null
  category: string | null
  categoryLabel: string | null
  score: number | null
  rank: number | null
  categoryRank: number | null
  tie: boolean
  decision: PassDecision
  decisionLabel: string
  computedDecision: PassDecision
  reasons: string[]
  details: { code: string; score: number | null; counted: boolean; note: string | null }[]
  manual: { reason: string; by: string; at: string | null } | null
}

export interface DisciplineColumn {
  code: string
  label: string
  coefficient: number
}

export interface RankingDetail {
  id: string
  stageId: string
  version: number
  status: RankingStatus
  statusLabel: string
  ruleVersion: number
  rules: PassRule
  reason: string | null
  computedBy: string
  computedAt: string | null
  summary: ResultSummary
  disciplines: DisciplineColumn[]
  entries: EntryView[]
  checks: { level: 'error' | 'warning' | 'ok'; message: string }[]
  visas: { role: string; by: string; at: string | null; note?: string | null }[]
  publications: { kind: PublicationKind; label: string; by: string; at: string | null }[]
  rectifications: Rectification[]
  approval: { note: string | null; at: string | null } | null
  can: {
    resolve: boolean
    submit: boolean
    returnToDraft: boolean
    approve: boolean
    reject: boolean
    isAuthor: boolean
    publish: boolean
    rectify: boolean
    decideRectification: boolean
  }
}

export interface Rectification {
  id: string
  rankingId: string
  status: RectificationStatus
  statusLabel: string
  reason: string
  requestedBy: string
  requestedAt: string | null
  decidedBy: string | null
  decidedAt: string | null
  decisionNote: string | null
  mine: boolean
}

export interface Queue {
  rankings: {
    id: string
    version: number
    status: RankingStatus
    statusLabel: string
    summary: Partial<ResultSummary>
    submittedAt: string | null
    approvedAt: string | null
    stage: { id: string; label: string }
    competition: { reference: string; title: string }
  }[]
  rectifications: {
    id: string
    rankingId: string
    reason: string
    requestedAt: string | null
    stage: { id: string; label: string }
    competition: { title: string }
  }[]
}

export function useQueue() {
  const { session } = useAuth()
  return useQuery({
    queryKey: [...RESULTS_KEY, 'queue'],
    queryFn: () => session.api.get<Queue>('/admin/results/queue'),
  })
}

export interface Simulation {
  ruleVersion: number
  rulesStatus: 'validated' | 'draft'
  summary: ResultSummary
  warnings: string[]
  disciplines: DisciplineColumn[]
  entries: EntryView[]
}

export interface Comparison {
  before: { id: string; version: number }
  after: { id: string; version: number }
  diff: RankingDiff[]
}

export function useResultsStages() {
  const { session } = useAuth()
  return useQuery({
    queryKey: [...RESULTS_KEY, 'stages'],
    queryFn: () =>
      session.api.get<{ data: ResultsStage[] }>('/admin/results/stages').then((r) => r.data),
  })
}

export function useResultsOverview(stageId: string) {
  const { session } = useAuth()
  return useQuery({
    queryKey: [...RESULTS_KEY, 'overview', stageId],
    queryFn: () => session.api.get<ResultsOverview>(`/admin/stages/${stageId}/results`),
  })
}

export function useRanking(id: string | null) {
  const { session } = useAuth()
  return useQuery({
    queryKey: [...RESULTS_KEY, 'ranking', id],
    queryFn: () => session.api.get<RankingDetail>(`/admin/rankings/${id}`),
    enabled: Boolean(id),
  })
}

export function useComparison(after: string | null, before: string | null) {
  const { session } = useAuth()
  return useQuery({
    queryKey: [...RESULTS_KEY, 'compare', after, before],
    queryFn: () => session.api.get<Comparison>(`/admin/rankings/${after}/compare/${before}`),
    enabled: Boolean(after && before && after !== before),
  })
}

export const DECISION_VARIANT: Record<
  PassDecision,
  'success' | 'info' | 'neutral' | 'danger' | 'warning' | 'outline'
> = {
  passed: 'success',
  supplementary: 'info',
  failed: 'neutral',
  eliminated: 'danger',
  incomplete: 'outline',
  absent: 'outline',
  pending: 'warning',
}

export const RANKING_VARIANT: Record<
  RankingStatus,
  'neutral' | 'info' | 'success' | 'warning' | 'outline'
> = {
  draft: 'neutral',
  under_control: 'info',
  approved: 'success',
  published: 'success',
  superseded: 'outline',
}

export const fmt = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : n.toLocaleString('fr-FR', { maximumFractionDigits: 2 })
